import { useState } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared cover-image renderer for list-view book cards.
 *
 * Phase 49: list endpoints (Marketplace, My Storybook, Continue Reading,
 * Moderation Queue, Trending) stopped inlining the cover thumbnail into
 * every response payload. Per-card cover bytes were ~50 KB of base64,
 * which on a saturated MongoDB tier was tipping marketplace requests
 * over the 30 s uvicorn worker timeout (~6.5 MB of disk reads × 130
 * books per request).
 *
 * Cards now point at `GET /api/storybooks/{book_id}/thumb` which streams
 * the WebP bytes with a 1-day browser cache + 1-week
 * stale-while-revalidate. Result: marketplace metadata response is ~5
 * KB, thumbs load in parallel from the dedicated URL, and the second
 * visit is essentially free.
 *
 * Backend may also return `book.thumb_url` directly - if absent we
 * derive it from `book.book_id` so older API responses still render.
 *
 * Falls back to the branded `📖 + title` placeholder when:
 *   • the response is 404 (thumb not yet generated - backend kicks off
 *     a backfill task; the next visit succeeds)
 *   • the network errors out
 *   • we don't have a book_id at all
 */
export default function BookCardCover({ book }) {
  const [errored, setErrored] = useState(false);
  const url = book?.thumb_url
    ? `${API_BASE}${book.thumb_url}`
    : book?.book_id
      ? `${API_BASE}/api/storybooks/${book.book_id}/thumb`
      : null;

  if (!url || errored) {
    return (
      <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
        <div className="text-center">
          <div className="text-4xl mb-2">📖</div>
          <p
            className="text-xs font-semibold"
            style={{ color: '#A68461', fontFamily: 'Fredoka' }}
          >
            {book?.title?.slice(0, 30) || 'Storybook'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={
        book?.title
          ? [
              `"${book.title}"`,
              book?.category && `${book.category} storybook`,
              book?.language && `in ${book.language}`,
              book?.age_group && `for ages ${book.age_group}`,
              book?.author_name && `by ${book.author_name}`,
            ].filter(Boolean).join(' · ')
          : 'Personalized AI children\'s storybook cover from Build Your Book'
      }
      className="w-full h-full object-cover"
      loading="lazy"
      onError={() => setErrored(true)}
      data-testid={`book-thumb-${book?.book_id || 'unknown'}`}
    />
  );
}
